/**
 * Mixin: Overlay
 * -----------------------------------------------------------------------------
 * A simple mixin to toggle window overlay with escape events.
 *
 * @namespace Overlay
 */
import cssClasses from '~/helpers/cssClasses';
import {extendDefaults, on} from '~/helpers/utils';

/**
 * Export overlay module as default.
 * @param {Object} config - Configuration.
 */
export default (config) => {

  /**
   * Instance default settings.
   */
  const defaults = {
    namespace: 'overlay',
    container: 'window-overlay',
  };

  /**
   * Instance settings.
   */
  const settings = extendDefaults(defaults, config);

  /**
   * Construct window overlay element on DOM.
   */
  function constructOverlay() {
    const element = document.createElement('div');
    element.classList.add(settings.container);
    element.setAttribute('js-overlay', settings.namespace);
    element.setAttribute('js-window', 'overlay');
    return element;
  }

  /**
   * Get window overlay element.
   * @param {String} namespace - The overlay namespace to find.
   */
  function getOverlay(namespace) {
    if (namespace) {
      return document.querySelector(`[js-overlay="${namespace}"]`);
    }

    return document.querySelector(`[js-window="overlay"]`);
  }

  /**
   * Update window overlay element if it already exists.
   */
  function updateOverlay() {
    const overlay = document.querySelector(`[js-window="overlay"]`);
    const currentOverlay = overlay.getAttribute('js-overlay');

    if (currentOverlay !== settings.namespace) {
      overlay.setAttribute('js-overlay', settings.namespace);
      Frame.EventBus.emit(`Toggle:${currentOverlay}:close`);
      setCloseEvents();
      return true;
    }

    return true;
  }

  /**
   * Open overlay and set esc events.
   */
  function open() {
    if (getOverlay()) {
      updateOverlay();
      return;
    }

    render();
    setCloseEvents();
  }

  /**
   * Hide overlay and remove esc event.
   */
  function close() {
    if (!getOverlay()) {
      return;
    }

    remove();
  }

  /**
   * Render overlay after constructing DOM element.
   */
  function render() {
    const windowOverlay = constructOverlay();
    document.body.appendChild(windowOverlay);

    /**
     * Dirty way to avoid MutationObserver by using an instant timeout.
     */
    window.setTimeout(() => windowOverlay.classList.add(cssClasses.active), 1);
    Frame.EventBus.emit(`Overlay:${settings.namespace}:open`);
  }

  /**
   * Remove overlay from DOM after transitioning out.
   */
  function remove() {
    on('transitionend', getOverlay(), () => {
      if (getOverlay()) {
        document.body.removeChild(getOverlay());
      }
    });

    /**
     * Only close overlay if toggle namespace overlay exists.
     */
    if (getOverlay(settings.namespace)) {
      getOverlay(settings.namespace).classList.remove(cssClasses.active);
    }
  }

  /**
   * Set close events.
   */
  function setCloseEvents() {
    on('click', getOverlay(settings.namespace), () => handleClickEvent());
  }

  /**
   * Handle click event on overlay.
   */
  function handleClickEvent() {
    Frame.EventBus.emit(`Overlay:${settings.namespace}:close`, {
      selector: settings.namespace,
      target: document.getElementById(settings.namespace),
    });

    close();
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    open,
    close,
  });
};
